import { sourceLabel } from "@/shared/ui/MarketplaceLogo";

export type UnreadSnapshot = {
  unread_threads: number;
  needs_reply: number;
  latest_inbound_at: string | null;
  latest_sender: string | null;
  latest_source: string | null;
};

export type NotificationPayload = { title: string; body: string };

/**
 * Decide se il poll corrente merita una notifica Windows/web.
 *
 * Regole (spec §2):
 * - Mai sul primo poll (prev null) → baseline.
 * - Solo se latest_inbound_at AVANZA rispetto al poll precedente.
 * - Solo se ci sono non-letti (unread_threads > 0).
 * - delta > 1 → testo plurale con il conteggio.
 * - delta <= 1 → mittente + canale. NB: delta 0 o negativo NON è spurio —
 *   latest_inbound_at è il last_message_at del thread non-letto più recente
 *   e può solo AVANZARE per un messaggio nuovo (il segna-letto al massimo
 *   lo fa arretrare, già escluso dal check). Delta 0 = nuovo messaggio in
 *   un thread già non-letto: va notificato (review 2026-06-11).
 * - Max 1 notifica per giro di poll (garantito dalla struttura).
 */
export function computeNotification(
  prev: UnreadSnapshot | null,
  next: UnreadSnapshot,
): NotificationPayload | null {
  if (prev === null) return null;
  if (next.unread_threads <= 0 || !next.latest_inbound_at) return null;

  const advanced =
    prev.latest_inbound_at === null ||
    new Date(next.latest_inbound_at) > new Date(prev.latest_inbound_at);
  if (!advanced) return null;

  const delta = next.unread_threads - prev.unread_threads;
  if (delta > 1) {
    return {
      title: `${delta} conversazioni con nuovi messaggi`,
      body: "Apri Messaggi per leggerle",
    };
  }

  const sender = next.latest_sender ?? "cliente";
  const channel = next.latest_source ? ` (${sourceLabel(next.latest_source)})` : "";
  return {
    title: `Nuovo messaggio da ${sender}${channel}`,
    body: `${next.unread_threads} conversazioni da leggere`,
  };
}
